/**
 * セルフサインアップ画面。
 *
 * 2 ステップで構成する:
 *   1. signUp: email + password を入力 → Cognito に新規ユーザを作成し、メールへ確認コードを送付。
 *   2. confirmSignUp: メールに届いた確認コードを入力 → ユーザを有効化。
 *
 * 完了後はログイン画面 (`/login`) に戻り、ユーザに改めてログインしてもらう。
 */
import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router';

import { APP_TITLE } from '../constants/appInfo';

import { useAuth } from './AuthProvider';

type Step = 'signUp' | 'confirm';

export function SignUpPage(): React.JSX.Element {
  const { signUp, confirmSignUp, resendConfirmationCode, error: authError } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>('signUp');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const handleSignUp = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (submitting) return;
    setLocalError(null);
    setInfo(null);
    if (password !== passwordConfirm) {
      setLocalError('パスワードと確認用パスワードが一致しません');
      return;
    }
    setSubmitting(true);
    try {
      await signUp(email, password);
      setStep('confirm');
      setInfo(`${email} 宛に確認コードを送信しました。メールをご確認ください。`);
    } catch {
      // エラーは AuthProvider 側で state に保存される
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirm = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (submitting) return;
    setLocalError(null);
    setInfo(null);
    setSubmitting(true);
    try {
      await confirmSignUp(email, code);
      // 完了後はログイン画面へ。
      navigate('/login', {
        replace: true,
        state: { signedUpEmail: email },
      });
    } catch {
      // エラーは AuthProvider 側で state に保存される
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async (): Promise<void> => {
    if (submitting || !email) return;
    setLocalError(null);
    setInfo(null);
    setSubmitting(true);
    try {
      await resendConfirmationCode(email);
      setInfo('確認コードを再送しました。メールをご確認ください。');
    } catch {
      // エラーは AuthProvider 側で state に保存される
    } finally {
      setSubmitting(false);
    }
  };

  const errorMessage = localError ?? authError;

  return (
    <div className="login-page">
      <div className="login-box">
        <h1>{APP_TITLE}</h1>
        <h2 className="login-subtitle">
          {step === 'signUp' ? 'アカウント作成' : 'メールアドレス確認'}
        </h2>

        {step === 'signUp' ? (
          <form onSubmit={handleSignUp}>
            <label>
              <span>メールアドレス</span>
              <input
                type="email"
                data-testid="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={submitting}
              />
            </label>
            <label>
              <span>パスワード (12文字以上 / 大小英字 + 数字)</span>
              <input
                type="password"
                data-testid="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={12}
                disabled={submitting}
              />
            </label>
            <label>
              <span>パスワード (確認)</span>
              <input
                type="password"
                data-testid="password-confirm"
                autoComplete="new-password"
                value={passwordConfirm}
                onChange={(e) => setPasswordConfirm(e.target.value)}
                required
                minLength={12}
                disabled={submitting}
              />
            </label>
            {info ? <div className="info-message">{info}</div> : null}
            {errorMessage ? <div className="error-message">{errorMessage}</div> : null}
            <button
              type="submit"
              data-testid="signup-submit"
              className="primary"
              disabled={submitting || !email || !password || !passwordConfirm}
            >
              {submitting ? '送信中...' : 'アカウントを作成'}
            </button>
            <div className="login-footer">
              すでにアカウントをお持ちの方は <Link to="/login">ログイン</Link>
            </div>
          </form>
        ) : (
          <form onSubmit={handleConfirm}>
            <label>
              <span>メールアドレス</span>
              <input
                type="email"
                data-testid="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={submitting}
              />
            </label>
            <label>
              <span>確認コード</span>
              <input
                type="text"
                data-testid="confirmation-code"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                required
                disabled={submitting}
              />
            </label>
            {info ? <div className="info-message">{info}</div> : null}
            {errorMessage ? <div className="error-message">{errorMessage}</div> : null}
            <button
              type="submit"
              data-testid="confirm-submit"
              className="primary"
              disabled={submitting || !email || !code}
            >
              {submitting ? '確認中...' : 'メールアドレスを確認'}
            </button>
            <div className="login-footer">
              <button
                type="button"
                data-testid="resend-code"
                onClick={handleResend}
                disabled={submitting || !email}
              >
                確認コードを再送
              </button>
              <Link to="/login">ログイン画面へ戻る</Link>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
